import { MatterListItemMin } from 'types/matters';

export enum DebriefingType {
    CASE_SPECIFIC = 'CASE_SPECIFIC',
    CLIENT_SPECIFIC = 'CLIENT_SPECIFIC',
    NON_CLIENT = 'NON_CLIENT',
}

export enum DebriefingLessonType {
    DID_WELL = 'DID_WELL',
    COULD_IMPROVE = 'COULD_IMPROVE',
}

export enum DebriefingLessonRelevance {
    CASE_SPECIFIC = 'CASE_SPECIFIC',
    CLIENT_WIDE = 'CLIENT_WIDE',
    PRACTICE_GROUP_WIDE = 'PRACTICE_GROUP_WIDE',
    FIRM_WIDE = 'FIRM_WIDE',
    UNCLEAR_OTHER = 'UNCLEAR_OTHER',
}

export const DEBRIEFING_LESSON_CATEGORY_CLASSIFIER = 'DEBRIEFING_LESSON_CATEGORY';

/** Every person/party reference the debriefing API returns. Employee-backed refs carry
 * firstName/lastName; party-backed refs only id + name. */
export interface DebriefingPersonRef {
    id: number;
    name: string;
    firstName: string | null;
    lastName: string | null;
}

export interface DebriefingMatterRef {
    id: number;
    caseNumber: string;
    name: string;
    isConfidential: boolean | null;
}

/** On accessible rows the full matter min DTO is embedded; on masked rows only the four
 * identity fields are populated (the by-ids matter reader applies no ACL). */
export type DebriefingListMatterRef = Pick<MatterListItemMin, 'id' | 'caseNumber' | 'name' | 'isConfidential'> &
    Partial<MatterListItemMin>;

export interface DebriefingLessonListItem {
    description: string;
    relevance: DebriefingLessonRelevance;
}

export interface DebriefingLessonDetails {
    id: number;
    lessonType: DebriefingLessonType;
    description: string;
    relevance: DebriefingLessonRelevance;
    categoryCodes: string[];
}

/** Write shape of a lesson; `sort_order` is the array index on save. */
export interface DebriefingLessonPayload {
    lessonType: DebriefingLessonType;
    description: string;
    relevance: DebriefingLessonRelevance;
    categoryCodes: string[];
}

/**
 * List row. `accessible` (BE `isAccessible`, primitive — serialized WITHOUT the `is` prefix,
 * unlike `isConfidential`) drives the restricted/masked rendering: when false the lessons and
 * task counts are null, `matters` carry identity fields only, and `accessContacts` (only then
 * populated) names who to ask for access.
 */
export interface DebriefingListElement {
    id: number;
    debriefingType: DebriefingType;
    debriefingDate: string;
    matters: DebriefingListMatterRef[] | null;
    clientParty: DebriefingPersonRef | null;
    projectName: string | null;
    isConfidential: boolean | null;
    lessonsDidWell: DebriefingLessonListItem[] | null;
    lessonsCouldImprove: DebriefingLessonListItem[] | null;
    taskTotalCount: number | null;
    taskDoneCount: number | null;
    accessible: boolean;
    accessContacts: DebriefingPersonRef[] | null;
    /** A draft may have no type or date yet, though the type says otherwise: every draft path reads them null-safe. */
    isDraft?: boolean;
}

export interface DebriefingDetails {
    id: number;
    debriefingType: DebriefingType;
    debriefingDate: string;
    matters: DebriefingMatterRef[] | null;
    clientParty: DebriefingPersonRef | null;
    projectName: string | null;
    sessionLeader: DebriefingPersonRef | null;
    participants: DebriefingPersonRef[] | null;
    supervisedBy: DebriefingPersonRef[] | null;
    responsibleLawyers: DebriefingPersonRef[] | null;
    relationshipPartners: DebriefingPersonRef[] | null;
    relationshipManagers: DebriefingPersonRef[] | null;
    context: string | null;
    isConfidential: boolean | null;
    isClientRepPresent: boolean | null;
    clientRepresentedBy: DebriefingPersonRef | null;
    clientFeedback: string | null;
    feedbackGivers: DebriefingPersonRef[] | null;
    scoreScopeMgmt: number | null;
    scoreTeamComposition: number | null;
    scoreDeadlinesMgmt: number | null;
    scoreBudgetMgmt: number | null;
    scoreCommunication: number | null;
    scoreGeneralPerformance: number | null;
    /** Server-computed on save from the first five scores; one decimal, null when none rated. */
    overallRating: number | null;
    lessons: DebriefingLessonDetails[] | null;
    accessible: boolean;
    accessContacts: DebriefingPersonRef[] | null;
    createdAt: string | null;
    createdByEmployee: DebriefingPersonRef | null;
    lastModifiedAt: string | null;
    changedByEmployee: DebriefingPersonRef | null;
    /** A draft may have no type or date yet, though the type says otherwise: every draft path reads them null-safe. */
    isDraft?: boolean;
}

export interface CreateOrUpdateDebriefing {
    debriefingType: DebriefingType;
    debriefingDate?: string;
    matterIds?: number[];
    clientPartyId?: number;
    projectName?: string;
    sessionLeaderEmployeeId?: number;
    participantEmployeeIds?: number[];
    supervisedByEmployeeIds?: number[];
    responsibleLawyerEmployeeIds?: number[];
    relationshipPartnerEmployeeIds?: number[];
    relationshipManagerEmployeeIds?: number[];
    context?: string;
    isConfidential?: boolean;
    isClientRepPresent?: boolean;
    clientRepresentedByPartyId?: number;
    clientFeedback?: string;
    feedbackGiverPartyIds?: number[];
    scoreScopeMgmt?: number | null;
    scoreTeamComposition?: number | null;
    scoreDeadlinesMgmt?: number | null;
    scoreBudgetMgmt?: number | null;
    scoreCommunication?: number | null;
    scoreGeneralPerformance?: number | null;
    lessons: DebriefingLessonPayload[];
}

/** Embedded shape on Task connections; no accessContacts — an inaccessible link only shows the lock. */
export interface DebriefingMin {
    id: number;
    debriefingType: DebriefingType;
    debriefingDate: string;
    clientParty: DebriefingPersonRef | null;
    projectName: string | null;
    isConfidential: boolean | null;
    accessible: boolean;
}

export interface DebriefingScoreAverage {
    average: number | null;
    ratedCount: number;
}

export interface DebriefingRecurringTag {
    /** DEBRIEFING_LESSON_CATEGORY classifier code — label must be resolved client-side. */
    categoryCode: string;
    debriefingCount: number;
}

/** Client-tab rollup. Deliberately not access-filtered: covers debriefings the viewer may not open. */
export interface DebriefingClientRollup {
    debriefingCount: number;
    scoreScopeMgmt: DebriefingScoreAverage;
    scoreTeamComposition: DebriefingScoreAverage;
    scoreDeadlinesMgmt: DebriefingScoreAverage;
    scoreBudgetMgmt: DebriefingScoreAverage;
    scoreCommunication: DebriefingScoreAverage;
    scoreGeneralPerformance: DebriefingScoreAverage;
    overallRating: DebriefingScoreAverage;
    recurringIssues: DebriefingRecurringTag[];
    recurringStrengths: DebriefingRecurringTag[];
    taskTotalCount: number;
    taskOpenCount: number;
}

/** Async-export row. Restricted rows are excluded server-side; the column set is the BE default
 * pending the open product decision. */
export interface DebriefingExportItem {
    id: number;
    debriefingType: DebriefingType;
    debriefingDate: string;
    matters: string[] | null;
    clientName: string | null;
    projectName: string | null;
    sessionLeader: string | null;
    participants: string[] | null;
    isConfidential: boolean | null;
    scoreScopeMgmt: number | null;
    scoreTeamComposition: number | null;
    scoreDeadlinesMgmt: number | null;
    scoreBudgetMgmt: number | null;
    scoreCommunication: number | null;
    scoreGeneralPerformance: number | null;
    overallRating: number | null;
    lessonsDidWell: DebriefingLessonListItem[] | null;
    lessonsCouldImprove: DebriefingLessonListItem[] | null;
    taskTotalCount: number | null;
    taskDoneCount: number | null;
}

export interface DebriefingRelationshipOwners {
    relationshipPartners: DebriefingPersonRef[];
    relationshipManagers: DebriefingPersonRef[];
}
